import { useState, useEffect } from "react";
import api from "@/lib/api";
import { ExternalLink } from "lucide-react";

const LABELS = {
  missiora: "Missiora",
  byteback_arcade: "ByteBack Arcade",
  byteback_gaming: "ByteBack Gaming",
};

export default function AdminCrossPromo() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/cross-promo/stats").then(({ data }) => setData(data)).catch(() => setErr(true));
  }, []);

  if (err) return <p className="p-5 text-sm text-donkey-grey">Could not load cross-promo stats.</p>;
  if (!data) return <p className="p-5 text-sm text-donkey-grey">Loading…</p>;

  const businesses = Object.entries(data.businesses || {});
  const trend = data.trend || [];
  const maxDay = Math.max(1, ...trend.map((t) => t.clicks));

  return (
    <div className="space-y-6" data-testid="admin-cross-promo">
      <div className="card-donkey rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <ExternalLink className="w-4 h-4 text-donkey-orange" />
          <h3 className="font-heading font-semibold text-donkey-warm text-sm">Cross-promotion clicks</h3>
        </div>
        <p className="text-xs text-donkey-grey">
          Aggregate only — destination, placement and timestamp. No names, emails, IPs or browser identifiers are stored.
          Total tracked clicks: <span className="text-donkey-warm font-mono">{data.total}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {businesses.map(([id, b]) => (
          <div key={id} className="card-donkey rounded-2xl p-5" data-testid={`cross-promo-${id}`}>
            <p className="text-xs uppercase tracking-wider text-donkey-grey mb-2">{LABELS[id] || id}</p>
            <p className="font-mono text-3xl text-donkey-warm mb-3">{b.total}</p>
            <p className="text-[11px] uppercase tracking-wider text-donkey-grey mb-1.5">Placements</p>
            {Object.keys(b.placements || {}).length === 0 && (
              <p className="text-xs text-donkey-grey">No clicks yet.</p>
            )}
            <ul className="space-y-1">
              {Object.entries(b.placements || {})
                .sort((a, c) => c[1] - a[1])
                .map(([p, n]) => (
                  <li key={p} className="flex items-center justify-between text-xs gap-3">
                    <span className="text-donkey-warm/80 truncate">{p}</span>
                    <span className="font-mono text-donkey-grey shrink-0">{n}</span>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="card-donkey rounded-2xl p-5">
        <p className="text-xs uppercase tracking-wider text-donkey-grey mb-4">Last 14 days</p>
        {trend.length === 0 && <p className="text-xs text-donkey-grey">No clicks recorded yet.</p>}
        <div className="flex items-end gap-1.5 h-32">
          {trend.map((t) => (
            <div key={t.day} className="flex-1 flex flex-col items-center gap-1.5 group" title={`${t.day}: ${t.clicks}`}>
              <span className="text-[10px] font-mono text-donkey-grey">{t.clicks}</span>
              <div
                className="w-full rounded-t bg-donkey-orange/70 group-hover:bg-donkey-orange transition-colors"
                style={{ height: `${(t.clicks / maxDay) * 100}%`, minHeight: t.clicks > 0 ? "4px" : "0" }}
              />
              <span className="text-[9px] text-donkey-grey">{t.day.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
